/**
 * V6 "SAVED" rail — granbay.com style.
 *
 * Hydrates the user's bookmarked markets from /v1/me/bookmarks
 * (BookmarkRow joined with market state) and renders them as a
 * horizontal snap-scroll rail on the v6 home, mirroring granbay.com's
 * "SAVED" section. Returns null for signed-out users (no rail) and
 * for authed users with zero bookmarks (no empty header — match v1's
 * pattern of hiding the section entirely until at least one is saved).
 *
 * Reuses V6MarketCard for the card visual, so the star toggle in the
 * corner stays in lockstep with the bookmark state.
 */
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Bookmark } from "lucide-react";
import {
  api,
  loadTokens,
  type BookmarkRow,
  type MarketListItem,
} from "@/lib/api";
import { V6MarketCard } from "@/components/v6/MarketCard";

const MAX = 8;

export function V6SavedRail() {
  const [bookmarks, setBookmarks] = useState<BookmarkRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!loadTokens()) {
        if (!cancelled) setBookmarks([]);
        return;
      }
      try {
        const rows = await api.listBookmarks();
        if (!cancelled) setBookmarks(rows);
      } catch {
        if (!cancelled) setBookmarks([]);
      }
    }

    void load();
    const refresh = () => void load();
    window.addEventListener("granbay.bookmark.changed", refresh);
    window.addEventListener("granbay.balance.changed", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("granbay.bookmark.changed", refresh);
      window.removeEventListener("granbay.balance.changed", refresh);
    };
  }, []);

  if (!bookmarks || bookmarks.length === 0) return null;

  return (
    <section className="mx-auto mt-6 w-full max-w-[1280px]">
      <div className="mb-3 flex items-baseline justify-between gap-3 px-4">
        <h2 className="inline-flex items-center gap-2 text-[16px] font-bold tracking-[-0.02em] sm:text-[18px]">
          <Bookmark
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            style={{ color: "var(--color-accent)", fill: "var(--color-accent)" }}
          />
          Saved
        </h2>
        <Link
          href="/home?tab=bookmarks"
          className="text-[12px] transition-colors sm:text-sm"
          style={{ color: "var(--color-text3)" }}
        >
          See all
        </Link>
      </div>
      <div
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-4"
        style={{ scrollbarWidth: "none" }}
      >
        {bookmarks.slice(0, MAX).map((b) => (
          <div
            key={b.marketId}
            className="w-[84%] flex-shrink-0 snap-start sm:w-auto"
          >
            <V6MarketCard market={bookmarkToMarket(b)} />
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Shape a BookmarkRow into the MarketListItem fields V6MarketCard
 * actually reads (id / slug / title / yesPriceCents / kind /
 * categorySlug). The rest are stubbed with sensible empties so the
 * type compiles — V6MarketCard never accesses volume / tradeCount /
 * priceHistory / etc.
 */
function bookmarkToMarket(b: BookmarkRow): MarketListItem {
  const kind =
    b.kind === "binary" || b.kind === "multi_outcome" || b.kind === "scalar"
      ? b.kind
      : "binary";
  return {
    id: b.marketId,
    slug: b.slug,
    title: b.title,
    description: { en: "" },
    imageUrl: b.imageUrl,
    kind,
    status: b.status,
    opensAt: "",
    closesAt: b.closesAt,
    resolvesAt: "",
    totalVolumeCents: "0",
    tradeCount: 0,
    uniqueTraders: 0,
    categorySlug: b.categorySlug,
    yesPriceCents: b.yesPriceCents,
    priceHistoryCents: null,
    lastTradeAt: null,
  };
}
